// src/pages/About.tsx
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  const { t, i18n } = useTranslation();

  // 依据全局语言（Navbar 切换）决定新增内容的显示语言（不新增到 i18n 文件）
  const isJA = i18n.language?.toLowerCase().startsWith('ja');

  const hardcoded = useMemo(() => {
    if (isJA) {
      return {
        // 会社概要
        companyTitle: '会社概要',
        companyNameLabel: '会社名',
        companyName: 'SMHC株式会社',
        companyAddrLabel: '所在地',
        companyAddr: '東京都新宿区西新宿７丁目２１−１ 新宿ロイヤルビル ２０１',
        inquiryLabel: 'お問い合わせ',
        inquiryText: 'お問い合わせフォーム',
        // 経営陣
        execTitle: '経営陣・取締役・アドバイザー',
        ceoName: '李　鉉道',
        ceoTitle: 'SMHC株式会社 代表取締役',
        // 医師紹介
        doctorTitle: '医師紹介',
        doctorName: '大日向 玲紀（おおひなた れいき）',
        doctorPhotoAlt: '医師・大日向玲紀',
        doctorPosition: '医療法人社団 大日向医院 理事長／日本医科大学卒業',
        doctorBadges: [
          '外科学会 外科専門医',
          '消化器外科学会 専門医',
          '内視鏡外科技術認定医',
          '臨床研修指導医',
          'オンライン診療研修修了',
          '日本医師会認定 産業医',
        ],
        // 提携
        partnerTitle: '提携クリニック',
        partnerClinicName: '大日向医院',
        partnerClinicIntro:
          '地域医療と専門診療の両立を重視し、生活者目線の医療提供を実践。オンライン診療においても安全性と実効性の高い体制を構築しています（詳細準備中）。',
      };
    }
    // 中文
    return {
      companyTitle: '公司信息',
      companyNameLabel: '公司名称',
      companyName: 'SMHC株式会社',
      companyAddrLabel: '注册地址',
      companyAddr: '东京都新宿区西新宿7丁目21-1 新宿Royal大楼201室',
      inquiryLabel: '联系我们',
      inquiryText: '在线联系表单',
      execTitle: '经营团队 / 董事 / 顾问',
      ceoName: '李　鉉道',
      ceoTitle: 'SMHC株式会社 代表董事',
      doctorTitle: '医生介绍',
      doctorName: '大日向 玲紀',
      doctorPhotoAlt: '医生 大日向玲纪',
      doctorPosition: '医疗法人社团 大日向医院 理事长／毕业于日本医科大学',
      doctorBadges: [
        '日本外科学会 外科专科医',
        '日本消化器外科学会 专科医',
        '内镜外科技术认证医',
        '临床研修指导医',
        '完成在线诊疗研修',
        '日本医师会认定 产业医',
      ],
      partnerTitle: '合作诊所',
      partnerClinicName: '大日向医院',
      partnerClinicIntro:
        '重视“区域医疗 + 专科诊疗”的平衡，以生活者视角提供服务。在在线诊疗方面亦建立了安全、有效的协作体系（详细信息准备中）。',
    };
  }, [isJA]);

  return (
    <div className="text-slate-800 font-sans bg-slate-50 min-h-screen">
      <Helmet>
        <title>{t('navbar.about')} - Melodis Clinic</title>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: hardcoded.doctorName.split('（')[0].trim(),
            alternateName: 'Reiki Ohinata',
            jobTitle: t('seo.person_job_title'),
            affiliation: {
              '@type': 'MedicalOrganization',
              name: t('seo.clinic_alternate_name'),
            },
            alumniOf: {
              '@type': 'CollegeOrUniversity',
              name: '日本医科大学',
            },
            description: t('seo.person_description'),
          })}
        </script>
      </Helmet>

      {/* Hero：珊瑚色グラデーション */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F97316] to-[#FB923C] px-6 py-16 md:py-24 text-center text-white">
        <div className="absolute -top-16 -left-16 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -right-16 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-3xl mx-auto relative z-10">
          <h1 className="text-2xl md:text-4xl font-bold mb-5 tracking-tight leading-tight">
            {t('about.title')}
          </h1>
          <div className="w-12 h-0.5 bg-white/40 mx-auto rounded-full mb-6" />
          <p className="text-sm md:text-base leading-relaxed max-w-xl mx-auto opacity-90">
            {t('about.subtitle')}
          </p>
        </div>
      </section>

      {/* Vision / Mission — 参考画像スタイル */}
      <section className="py-10 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-6 text-center">
            {isJA ? '私たちの使命と目指す姿' : '我们的使命与愿景'}
          </h2>
          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            {/* 左：テキストカード */}
            <div className="flex flex-col gap-5">
              {/* Mission */}
              <div className="flex-1 rounded-2xl border border-rose-200 bg-white shadow-sm p-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <img src="/favicon.ico" alt="" className="h-7 w-7" />
                    <span className="text-coral font-extrabold text-xl tracking-wide">Mission</span>
                    <span className="text-sm text-slate-400">{isJA ? '（使命）' : '（使命）'}</span>
                  </div>
                  <p className="font-bold text-slate-800 text-xl sm:text-2xl leading-relaxed mb-4">
                    {isJA ? (
                      <>医療とテクノロジーで、<br />健康な人生をデザインする</>
                    ) : (
                      <>以医疗与技术，<br />设计健康的人生</>
                    )}
                  </p>
                </div>
                <p className="text-slate-400 text-sm italic leading-relaxed border-t border-rose-100 pt-4">
                  Designing Healthy Life with Medical and Technology
                </p>
              </div>

              {/* Vision */}
              <div className="flex-1 rounded-2xl border border-slate-200 bg-white shadow-sm p-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <img src="/favicon.ico" alt="" className="h-7 w-7 opacity-55" />
                    <span className="text-slate-600 font-extrabold text-xl tracking-wide">Vision</span>
                    <span className="text-sm text-slate-400">{isJA ? '（目指す姿）' : '（愿景）'}</span>
                  </div>
                  <p className="font-bold text-slate-800 text-xl sm:text-2xl leading-relaxed mb-4">
                    {isJA ? (
                      <>世界中の人が、どこにでも<br />最適な医療を受けられる社会をつくる。</>
                    ) : (
                      <>打造世界上任何人<br />都能获得最佳医疗的社会。</>
                    )}
                  </p>
                </div>
                <p className="text-slate-400 text-sm italic leading-relaxed border-t border-slate-100 pt-4">
                  Creating a world where everyone can access the right healthcare, anywhere.
                </p>
              </div>
            </div>

            {/* 右：画像（枠付き独立ブロック） */}
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm min-h-[300px] md:min-h-0">
              <img
                src="/images/mission-vision-bg.png"
                alt="mission vision"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 三个特徴卡片（保持 i18n） */}
      <section className="py-6 sm:py-14 px-0 sm:px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white border-y sm:border border-slate-100 rounded-none sm:rounded-3xl p-5 sm:p-7 shadow-none sm:shadow-sm hover:shadow-md transition"
              >
                <div className="mb-2 text-coral text-sm font-semibold tracking-wide">
                  {t(`about.feature_${i}_title`)}
                </div>
                <div className="h-1 w-12 rounded-full bg-coral/40 mb-4" />
                <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                  {t(`about.feature_${i}_text`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 会社概要（新增：硬编码内容，随全局语言变化） */}
      <section className="py-6 sm:py-14 px-0 sm:px-4">
        <div className="max-w-6xl mx-auto bg-white rounded-none sm:rounded-3xl border-y sm:border border-slate-100 shadow-none sm:shadow-md p-5 sm:p-9">
          <h2 className="text-xl font-semibold mb-6 pl-3 border-l-4 border-coral">
            {hardcoded.companyTitle}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <dl className="divide-y divide-slate-100 border border-slate-100 rounded-2xl">
                <div className="grid grid-cols-3 gap-4 p-4">
                  <dt className="col-span-1 text-sm text-slate-500">{hardcoded.companyNameLabel}</dt>
                  <dd className="col-span-2 text-sm md:text-base text-slate-800">{hardcoded.companyName}</dd>
                </div>
                <div className="grid grid-cols-3 gap-4 p-4">
                  <dt className="col-span-1 text-sm text-slate-500">{hardcoded.companyAddrLabel}</dt>
                  <dd className="col-span-2 text-sm md:text-base text-slate-800">{hardcoded.companyAddr}</dd>
                </div>
                <div className="grid grid-cols-3 gap-4 p-4">
                  <dt className="col-span-1 text-sm text-slate-500">{hardcoded.inquiryLabel}</dt>
                  <dd className="col-span-2">
                    <Link
                      to="/contact"
                      className="inline-flex items-center text-sm md:text-base text-coral hover:opacity-80 underline underline-offset-2"
                    >
                      {hardcoded.inquiryText}
                    </Link>
                  </dd>
                </div>
              </dl>
            </div>

            {/* 経営陣／经营团队 */}
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6">
              <h3 className="text-base font-semibold text-slate-800 mb-4">
                {hardcoded.execTitle}
              </h3>
              <div className="space-y-1">
                <p className="text-lg font-bold">{hardcoded.ceoName}</p>
                <p className="text-slate-700">{hardcoded.ceoTitle}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 医師紹介（新增：证件照排版 + 资格清单） */}
      <section className="py-6 sm:py-14 px-0 sm:px-4">
        <div className="max-w-6xl mx-auto bg-white rounded-none sm:rounded-3xl border-y sm:border border-slate-100 shadow-none sm:shadow-md p-5 sm:p-9">
          <h2 className="text-xl font-semibold mb-6 pl-3 border-l-4 border-coral">
            {hardcoded.doctorTitle}
          </h2>

          <div className="grid md:grid-cols-[220px,1fr] gap-8 items-start">
            <div className="w-[200px] md:w-[220px] mx-auto md:mx-0">
              <div className="bg-slate-100 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src="/images/doctor01.jpeg"
                  alt={hardcoded.doctorPhotoAlt}
                  className="w-full h-auto"
                />
              </div>
            </div>

            <div>
              <p className="text-lg font-bold mb-1">{hardcoded.doctorName}</p>
              <p className="text-slate-700 mb-4">{hardcoded.doctorPosition}</p>
              <ul className="grid sm:grid-cols-2 gap-2">
                {hardcoded.doctorBadges.map((b, i) => (
                  <li
                    key={i}
                    className="text-sm md:text-base text-slate-800 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 提携クリニック（占位） */}
      <section className="py-6 sm:py-14 px-0 sm:px-4">
        <div className="max-w-6xl mx-auto bg-white rounded-none sm:rounded-3xl border-y sm:border border-slate-100 shadow-none sm:shadow-md p-5 sm:p-9">
          <h2 className="text-xl font-semibold mb-6 pl-3 border-l-4 border-coral">
            {hardcoded.partnerTitle}
          </h2>
          <div className="space-y-2">
            <p className="text-lg font-bold">{hardcoded.partnerClinicName}</p>
            <p className="text-slate-700 text-sm md:text-base leading-relaxed">
              {hardcoded.partnerClinicIntro}
            </p>
          </div>
        </div>
      </section>

      {/* 页面不再重复版权语句，交由全站 Footer 统一显示 */}
      <footer className="h-6" />
    </div>
  );
};

export default About;
